import { productAPI } from 'api';
import config from 'config';
import { normalize, schema } from 'normalizr';
import { Dispatch } from 'redux';
import { parseItems } from 'store/helpers';
import { EntitiesState, FuneralType, ProductCategory, GetState } from 'types';
import { createAction } from 'typesafe-actions';

interface FetchProductAction {
  entities: Pick<EntitiesState, 'products'>;
  result: {
    [key in ProductCategory]: string;
  };
}

interface FetchProductsAction {
  entities: Pick<EntitiesState, 'products'>;
  result: string[];
}

const product = new schema.Entity(
  'products',
  {},
  { idAttribute: 'sku', processStrategy: parseItems },
);

const fetchProductAction = createAction('FETCH_PRODUCT')<FetchProductAction>();
const fetchAlternativeProductsAction = createAction(
  'FETCH_ALTERNATIVE_PRODUCTS',
)<FetchProductsAction>();

const fetchFavoriteProductsAction = createAction('FETCH_FAVORITE_PRODUCTS')<FetchProductsAction>();

export default {
  fetchAlternativeProducts: fetchAlternativeProductsAction,
  fetchFavoriteProducts: fetchFavoriteProductsAction,
  fetchProduct: fetchProductAction,
};

export const fetchProduct = (id: string, pricebookId: string) => async (dispatch: Dispatch) => {
  const data = await productAPI.fetch(id, pricebookId);

  return dispatch(
    fetchProductAction(normalize({ [data.category]: data }, { [data.category]: product })),
  );
};

/*
 * Fetch products for given category which then can be presented in product catalouge card
 * */

export const fetchFavoriteProducts =
  ({
    category,
    type,
    isFavorite,
    pricebookId = config.defaultPricebook,
  }: {
    category: ProductCategory;
    type: FuneralType;
    isFavorite?: boolean;
    pricebookId?: string;
  }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const products = await productAPI.fetchAll({
      category,
      isFavorite,
      type,
      ...(pricebookId ? { pricebookId } : { pricebookId: config.defaultPricebook }),
    });

    return dispatch(
      fetchFavoriteProductsAction(
        normalize({ [type]: { [category]: products } }, { [type]: { [category]: [product] } }),
      ),
    );
  };

/*
 * Fetch products for given category which then can be presented in product details modal window
 * */
export const fetchAlternativeProducts =
  ({ category, type }: { category: ProductCategory; type?: FuneralType }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const {
      proposal: { salesforce },
    } = getState();

    const pricebookId = salesforce?.pricebookId;

    const products = await productAPI.fetchAll({
      category,
      type,
      ...(pricebookId ? { pricebookId } : { pricebookId: config.defaultPricebook }),
    });

    return dispatch(
      fetchAlternativeProductsAction(
        normalize(
          { [type || 'all']: { [category]: products } },
          { [type || 'all']: { [category]: [product] } },
        ),
      ),
    );
  };
